import React, { useEffect, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as Sentry from "@sentry/browser";
import PropTypes from "prop-types";

import { TextField } from "@material-ui/core";

import { makeRequest } from "./util";

type AutocompletePropResponse = string[];

// TODO: We can make this component's contract more specific by
// explicitly typing the props it takes in under `...rest` to target
// the delegation of props to the underlying TextField.
export type AutocompletePropFieldProps = {
  propName: string;
  value: string;
  onChange: (value: string) => void;
} & React.ComponentProps<typeof TextField>;

function AutocompletePropField({
  propName,
  value,
  onChange,
  ...rest
}: AutocompletePropFieldProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<string[]>([]);
  const [visibleOptions, setVisibleOptions] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setVisibleOptions(options);
    } else {
      setVisibleOptions([]);
    }
  }, [open, options]);

  useEffect(() => {
    if (!propName) {
      setOptions([]);
      return;
    }

    makeRequest<AutocompletePropResponse>("autocomplete_prop", {
      prop_name: propName,
      prop_value: value,
    })
      .then((response) => {
        setOptions(response.data);
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
      });
  }, [propName, value]);

  return (
    <Autocomplete
      id="Value"
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      options={visibleOptions}
      // When first navigating to an event, onInputChange seems to be
      // fired even though it shouldn't be. This is causing issues with
      // the event being marked as edited when it shouldn't be.
      onInputChange={(event, value) => event && onChange(value)}
      freeSolo={true}
      disableClearable={true}
      loading={false}
      renderInput={(params) => (
        <TextField value={value} {...params} {...rest} />
      )}
      disabled={rest.disabled}
    />
  );
}

AutocompletePropField.propTypes = {
  propName: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default AutocompletePropField;
